import { useState, useEffect } from 'react';
import { ProxyImage } from '../../lib/imageProxy';
import {
  Plus,
  Trash2,
  Edit2,
  Link as LinkIcon,
  Image as ImageIcon,
  GripVertical,
  Eye,
  EyeOff,
  X,
  Check,
  ExternalLink,
  Upload,
  AlertCircle,
  Calendar,
  Type
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

interface SliderImage {
  id: string;
  image: string;
  link: string;
  title: string | null;
  description: string | null;
  order: number;
  is_active: boolean;
  created_at: string;
}

export default function SlidersTab() {
  const [sliderImages, setSliderImages] = useState<SliderImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingImage, setEditingImage] = useState<SliderImage | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    link: '',
    title: '',
    description: '',
    is_active: true
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, []);

  useEffect(() => {
    fetchSliderImages();
  }, []);

  const fetchSliderImages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('slider_images')
        .select('*')
        .order('"order"', { ascending: true });

      if (error) throw error;
      setSliderImages(data || []);
    } catch (error) {
      console.error('Error fetching slider images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  const convertToWebP = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (!event.target?.result) {
          reject(new Error('Error al leer el archivo'));
          return;
        }

        const img = new Image();
        img.onload = () => {
          const targetWidth = 1920;
          const targetHeight = 640;

          const canvas = document.createElement('canvas');
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('No se pudo crear el contexto del canvas'));
            return;
          }

          ctx.fillStyle = '#000000';
          ctx.fillRect(0, 0, targetWidth, targetHeight);

          const imgRatio = img.width / img.height;
          const targetRatio = targetWidth / targetHeight;

          let drawWidth, drawHeight, xOffset, yOffset;

          if (imgRatio > targetRatio) {
            drawHeight = targetHeight;
            drawWidth = drawHeight * imgRatio;
            xOffset = (targetWidth - drawWidth) / 2;
            yOffset = 0;
          } else {
            drawWidth = targetWidth;
            drawHeight = drawWidth / imgRatio;
            xOffset = 0;
            yOffset = (targetHeight - drawHeight) / 2;
          }

          ctx.drawImage(img, xOffset, yOffset, drawWidth, drawHeight);

          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Error al convertir a WebP'));
            }
          }, 'image/webp', 0.95);
        };

        img.onerror = () => reject(new Error('Error al cargar la imagen'));
        img.src = event.target.result as string;
      };

      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsDataURL(file);
    });
  };

  const uploadImage = async () => {
    if (!selectedFile) {
      alert('Por favor selecciona una imagen');
      return;
    }

    try {
      setUploading(true);

      const webpBlob = await convertToWebP(selectedFile);
      const fileName = `${uuidv4()}.webp`;
      const filePath = `slider/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('public')
        .upload(filePath, webpBlob, {
          contentType: 'image/webp',
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('public')
        .getPublicUrl(filePath);

      const newOrder = sliderImages.length > 0
        ? Math.max(...sliderImages.map(img => img.order)) + 1
        : 1;

      const { error: dbError } = await supabase
        .from('slider_images')
        .insert([{
          image: publicUrl,
          link: formData.link || '#',
          title: formData.title || null,
          description: formData.description || null,
          is_active: formData.is_active,
          order: newOrder
        }]);

      if (dbError) throw dbError;

      fetchSliderImages();
      resetForm();
      setShowAddForm(false);

    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFormData({ link: '', title: '', description: '', is_active: true });
  };

  const deleteImage = async (id: string) => {
    if (!confirm('¿Eliminar esta imagen del slider?')) return;

    try {
      const { data: imageData } = await supabase
        .from('slider_images')
        .select('image')
        .eq('id', id)
        .single();

      if (imageData?.image) {
        const urlParts = imageData.image.split('/');
        const fileName = urlParts[urlParts.length - 1];
        const filePath = `slider/${fileName}`;

        await supabase.storage.from('public').remove([filePath]);
      }

      const { error } = await supabase
        .from('slider_images')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchSliderImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Error al eliminar la imagen');
    }
  };

  const toggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('slider_images')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      setSliderImages(prev =>
        prev.map(img =>
          img.id === id ? { ...img, is_active: !currentStatus } : img
        )
      );
    } catch (error) {
      console.error('Error toggling slider status:', error);
    }
  };

  const updateImageOrder = async (id: string, direction: 'up' | 'down') => {
    const currentIndex = sliderImages.findIndex(img => img.id === id);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= sliderImages.length) return;

    const currentImage = sliderImages[currentIndex];
    const targetImage = sliderImages[targetIndex];

    try {
      await supabase
        .from('slider_images')
        .update({ order: targetImage.order })
        .eq('id', currentImage.id);

      await supabase
        .from('slider_images')
        .update({ order: currentImage.order })
        .eq('id', targetImage.id);

      fetchSliderImages();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const startEditing = (image: SliderImage) => {
    setEditingImage(image);
    setFormData({
      link: image.link,
      title: image.title || '',
      description: image.description || '',
      is_active: image.is_active
    });
  };

  const saveEdit = async () => {
    if (!editingImage) return;

    try {
      setUploading(true);

      let updateData: any = {
        link: formData.link,
        title: formData.title || null,
        description: formData.description || null,
        is_active: formData.is_active
      };

      if (selectedFile) {
        const webpBlob = await convertToWebP(selectedFile);
        const fileName = `${uuidv4()}.webp`;
        const filePath = `slider/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('public')
          .upload(filePath, webpBlob, {
            contentType: 'image/webp',
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('public')
          .getPublicUrl(filePath);

        updateData.image = publicUrl;
      }

      const { error } = await supabase
        .from('slider_images')
        .update(updateData)
        .eq('id', editingImage.id);

      if (error) throw error;

      fetchSliderImages();
      cancelEdit();
    } catch (error) {
      console.error('Error updating slider:', error);
      alert('Error al actualizar');
    } finally {
      setUploading(false);
    }
  };

  const cancelEdit = () => {
    setEditingImage(null);
    resetForm();
  };

  const activeCount = sliderImages.filter(img => img.is_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Slider Principal</h2>
          <p className="text-sm text-gray-500 mt-1">
            {sliderImages.length} {sliderImages.length === 1 ? 'imagen' : 'imágenes'} ({activeCount} activas)
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Agregar imagen
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{sliderImages.length}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
              <p className="text-xs text-gray-500">Activas</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
              <EyeOff className="w-5 h-5 text-gray-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{sliderImages.length - activeCount}</p>
              <p className="text-xs text-gray-500">Inactivas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold">Nueva imagen de slider</h3>
              <button
                onClick={() => { setShowAddForm(false); resetForm(); }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imagen *
                </label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl overflow-hidden">
                  {previewUrl ? (
                    <div className="relative aspect-[3/1]">
                      <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        onClick={() => { setPreviewUrl(null); setSelectedFile(null); }}
                        className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-48 cursor-pointer hover:bg-gray-50 transition-colors">
                      <Upload className="w-10 h-10 text-gray-300 mb-3" />
                      <p className="text-sm text-gray-500">Haz clic para subir una imagen</p>
                      <p className="text-xs text-gray-400 mt-1">Recomendado: 1920x640px</p>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título (opcional)
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ej: Promoción de verano"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enlace (URL)
                </label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.link}
                    onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                    placeholder="https://ejemplo.com/pagina"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción (opcional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descripción breve de la imagen..."
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Estado activo</p>
                  <p className="text-sm text-gray-500">La imagen se mostrará en el slider</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    formData.is_active ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      formData.is_active ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => { setShowAddForm(false); resetForm(); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={uploadImage}
                disabled={uploading || !selectedFile}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Guardar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold">Editar imagen</h3>
              <button
                onClick={cancelEdit}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Current/New Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imagen
                </label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl overflow-hidden">
                  {previewUrl ? (
                    <div className="relative aspect-[3/1]">
                      <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        onClick={() => { setPreviewUrl(null); setSelectedFile(null); }}
                        className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative aspect-[3/1]">
                      <ProxyImage
                        src={editingImage.image}
                        alt="Current"
                        className="w-full h-full object-cover"
                      />
                      <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                        <div className="text-center text-white">
                          <Upload className="w-8 h-8 mx-auto mb-2" />
                          <p className="text-sm">Cambiar imagen</p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Título del slider"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enlace
                </label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.link}
                    onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                    placeholder="https://ejemplo.com/pagina"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descripción breve..."
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Estado activo</p>
                  <p className="text-sm text-gray-500">La imagen se mostrará en el slider</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    formData.is_active ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      formData.is_active ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={cancelEdit}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={saveEdit}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Guardar cambios
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Images Grid */}
      <div className="bg-white rounded-xl border border-gray-100">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sliderImages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <ImageIcon className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-gray-900 font-medium mb-1">Sin imágenes</h3>
            <p className="text-sm text-gray-500 mb-4">Agrega tu primera imagen al slider</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Agregar imagen
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {sliderImages.map((image, index) => (
              <div
                key={image.id}
                className={`flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors ${
                  !image.is_active ? 'opacity-60' : ''
                }`}
              >
                {/* Order Controls */}
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => updateImageOrder(image.id, 'up')}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <span className="text-xs text-gray-400 text-center">{index + 1}</span>
                  <button
                    onClick={() => updateImageOrder(image.id, 'down')}
                    disabled={index === sliderImages.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {/* Image Thumbnail */}
                <div className="w-32 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  <ProxyImage
                    src={image.image}
                    alt={image.title || `Slider ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {image.title ? (
                      <h4 className="font-medium text-gray-900 truncate">{image.title}</h4>
                    ) : (
                      <h4 className="text-gray-400 italic">Sin título</h4>
                    )}
                    {!image.is_active && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                        Inactivo
                      </span>
                    )}
                  </div>
                  {image.link && image.link !== '#' && (
                    <a
                      href={image.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                    >
                      <LinkIcon className="w-3 h-3" />
                      <span className="truncate max-w-xs">{image.link}</span>
                    </a>
                  )}
                  {image.description && (
                    <p className="text-sm text-gray-500 truncate mt-1">{image.description}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Toggle Active */}
                  <button
                    onClick={() => toggleActive(image.id, image.is_active)}
                    className={`p-2 rounded-lg transition-colors ${
                      image.is_active
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={image.is_active ? 'Desactivar' : 'Activar'}
                  >
                    {image.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>

                  {/* Edit */}
                  <button
                    onClick={() => startEditing(image)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>

                  {/* Preview Link */}
                  {image.link && image.link !== '#' && (
                    <a
                      href={image.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Ver enlace"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}

                  {/* Delete */}
                  <button
                    onClick={() => deleteImage(image.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium mb-1">Consejos para el slider</p>
          <ul className="list-disc list-inside space-y-1 text-blue-600">
            <li>Las imágenes se redimensionan automáticamente a 1920x640px</li>
            <li>Usa imágenes con proporción 3:1 para mejores resultados</li>
            <li>Solo las imágenes activas se muestran en el sitio</li>
            <li>Arrastra para reordenar las imágenes</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
